import React, { useMemo, useState } from 'react';
import { Users2, Circle, Search, Phone, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useAgentPresence, type AgentStatus } from '@/hooks/useAgentPresence';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const STATUS_META: Record<AgentStatus, { label: string; dot: string; text: string }> = {
  available: { label: 'Disponible', dot: 'bg-emerald-400', text: 'text-emerald-400' },
  busy:      { label: 'Occupé',     dot: 'bg-red-400',     text: 'text-red-400' },
  away:      { label: 'Absent',     dot: 'bg-amber-400',   text: 'text-amber-400' },
  offline:   { label: 'Hors ligne', dot: 'bg-white/20',    text: 'text-white/30' },
};

export function AgentsOnlinePanel() {
  const { agents, availableAgents, busyAgents, awayAgents, loading } = useAgentPresence();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | AgentStatus>('all');

  const filtered = useMemo(() => {
    return agents
      .filter(a => filter === 'all' || a.status === filter)
      .filter(a => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (a.displayName || '').toLowerCase().includes(q) || (a.role || '').toLowerCase().includes(q);
      })
      .sort((a, b) => {
        const order: AgentStatus[] = ['available', 'busy', 'away', 'offline'];
        return order.indexOf(a.status) - order.indexOf(b.status);
      });
  }, [agents, filter, search]);

  return (
    <div className="h-full flex flex-col">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 p-4 border-b border-white/10">
        <StatCard label="Disponibles" value={availableAgents.length} color="text-emerald-400" dot="bg-emerald-400" />
        <StatCard label="Occupés"     value={busyAgents.length}      color="text-red-400"     dot="bg-red-400" />
        <StatCard label="Absents"     value={awayAgents.length}      color="text-amber-400"   dot="bg-amber-400" />
      </div>

      {/* Filters */}
      <div className="px-4 py-2 flex items-center gap-2 border-b border-white/10">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
          <Input placeholder="Rechercher un agent..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs bg-white/5 border-white/10 text-white placeholder:text-white/30" />
        </div>
        {(['all', 'available', 'busy', 'away'] as const).map(f => (
          <Button key={f} size="sm" variant="ghost"
            className={`h-8 text-[11px] ${filter === f ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
            onClick={() => setFilter(f)}>
            {f === 'all' ? 'Tous' : STATUS_META[f].label}
          </Button>
        ))}
      </div>

      {/* List */}
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="w-5 h-5 animate-spin text-white/30" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-white/30">
            <Users2 className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-xs">Aucun agent</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map(a => {
              const meta = STATUS_META[a.status];
              const initials = (a.displayName || '?')
                .split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
              const lastSeen = a.lastSeen
                ? formatDistanceToNow(new Date(a.lastSeen), { addSuffix: true, locale: fr })
                : '—';
              return (
                <div key={a.userId} className="px-4 py-3 hover:bg-white/[0.03] transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                        <span className="text-xs font-bold text-white/60">{initials || '?'}</span>
                      </div>
                      <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[hsl(220,18%,13%)] ${meta.dot}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{a.displayName || 'Sans nom'}</span>
                        {a.role && <Badge variant="outline" className="text-[9px] h-4 border-white/10 text-white/50">{a.role}</Badge>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Circle className={`w-2 h-2 fill-current ${meta.text}`} />
                        <span className={`text-[11px] ${meta.text}`}>{meta.label}</span>
                        <span className="text-[10px] text-white/30">• vu {lastSeen}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

function StatCard({ label, value, color, dot }: { label: string; value: number; color: string; dot: string }) {
  return (
    <div className="bg-white/5 rounded-xl p-3 border border-white/5">
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        <span className="text-[10px] text-white/40 uppercase tracking-wider">{label}</span>
      </div>
      <span className={`text-xl font-bold ${color}`}>{value}</span>
    </div>
  );
}
